import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BarChart3, Download, Music2, PlayCircle, TrendingUp, Eye, Heart, Video, FileDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

function SearchInput({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative mb-3">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Buscar..."}
        className="w-full bg-black border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF007F] focus:outline-none transition"
        data-testid={testId}
      />
    </div>
  );
}

function Pagination({ total, page, onPage, idPrefix }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
      <span className="text-xs text-zinc-500" data-testid={`${idPrefix}-page-info`}>
        Página <span className="text-white font-bold tabular-nums">{page}</span> de{" "}
        <span className="text-white font-bold tabular-nums">{totalPages}</span>{" "}
        <span className="opacity-60">· {total} itens</span>
      </span>
      <div className="flex gap-1">
        <button
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition"
          data-testid={`${idPrefix}-prev`}
          aria-label="Página anterior"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          onClick={() => onPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition"
          data-testid={`${idPrefix}-next`}
          aria-label="Próxima página"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, accent = "#FF007F", testId }) {
  const formatted = typeof value === "number" ? value.toLocaleString("pt-BR") : value;
  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5 flex items-center gap-4" data-testid={testId}>
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${accent}20`, color: accent }}
      >
        <Icon size={22} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold">{label}</p>
        <p className="text-2xl font-black text-white truncate tabular-nums">{formatted}</p>
      </div>
    </div>
  );
}

function TopList({ title, items, countKey, coverKey, idPrefix }) {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const t = (it.cd_title || it.title || "").toLowerCase();
      const a = (it.artist_name || "").toLowerCase();
      return t.includes(q) || a.includes(q);
    });
  }, [items, query]);

  // Reset page when query changes
  useEffect(() => { setPage(1); }, [query]);

  if (!items || items.length === 0) {
    return (
      <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5">
        <h3 className="font-bold text-lg text-white mb-3">{title}</h3>
        <p className="text-zinc-500 text-sm">Sem dados ainda no período.</p>
      </div>
    );
  }
  const max = Math.max(...items.map((i) => i[countKey] || 0), 1);
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);
  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5">
      <h3 className="font-bold text-lg text-white mb-3">{title}</h3>
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder="Buscar por título ou artista..."
        testId={`${idPrefix || "list"}-search`}
      />
      {filtered.length === 0 ? (
        <p className="text-zinc-500 text-sm py-6 text-center">Nenhum resultado para “{query}”.</p>
      ) : (
        <>
          <div className="space-y-3">
            {pageItems.map((it, localIdx) => {
              const idx = start + localIdx;
              const pct = ((it[countKey] || 0) / max) * 100;
              return (
                <div key={idx} className="flex items-center gap-3" data-testid={`stats-top-${title.replace(/\s+/g,'-').toLowerCase()}-${idx}`}>
                  <span className="text-[11px] text-zinc-500 w-6 text-right font-bold tabular-nums">{idx + 1}</span>
                  {coverKey && it[coverKey] ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${it[coverKey]}`} alt="" className="w-10 h-10 rounded-md object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-white/5 flex-shrink-0 flex items-center justify-center">
                      <Music2 size={14} className="text-zinc-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {it.cd_title || it.title || "?"}
                    </p>
                    <p className="text-zinc-500 text-xs truncate">{it.artist_name}</p>
                    <div className="mt-1 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#FF007F]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <span className="text-[#FF007F] font-bold text-sm tabular-nums flex-shrink-0">
                    {typeof it[countKey] === "number" ? it[countKey].toLocaleString("pt-BR") : (it[countKey] || 0)}
                  </span>
                </div>
              );
            })}
          </div>
          <Pagination total={filtered.length} page={page} onPage={setPage} idPrefix={idPrefix || "list"} />
        </>
      )}
    </div>
  );
}

function TopVideosList({ items }) {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((v) => {
      const t = (v.title || "").toLowerCase();
      const a = (v.artist_name || "").toLowerCase();
      return t.includes(q) || a.includes(q);
    });
  }, [items, query]);

  useEffect(() => { setPage(1); }, [query]);

  if (!items || items.length === 0) {
    return (
      <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5">
        <h3 className="font-bold text-lg text-white mb-3">Top Vídeos da Vitrine</h3>
        <p className="text-zinc-500 text-sm">Sem dados ainda. Aguardando views.</p>
      </div>
    );
  }
  const max = Math.max(...items.map((i) => i.views || 0), 1);
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);
  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5">
      <h3 className="font-bold text-lg text-white mb-3 flex items-center gap-2">
        <Video size={18} className="text-[#FF007F]" /> Top Vídeos da Vitrine
      </h3>
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder="Buscar vídeo por título ou artista..."
        testId="videos-search"
      />
      {filtered.length === 0 ? (
        <p className="text-zinc-500 text-sm py-6 text-center">Nenhum vídeo encontrado para “{query}”.</p>
      ) : (
        <>
          <div className="space-y-3">
            {pageItems.map((v, localIdx) => {
              const idx = start + localIdx;
              const pct = ((v.views || 0) / max) * 100;
              const thumb = v.thumbnail_url
                ? (v.thumbnail_url.startsWith("http") ? v.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${v.thumbnail_url}`)
                : null;
              return (
                <div key={v.id || idx} className="flex items-center gap-3" data-testid={`stats-top-video-${idx}`}>
                  <span className="text-[11px] text-zinc-500 w-6 text-right font-bold tabular-nums">{idx + 1}</span>
                  {thumb ? (
                    <img src={thumb} alt="" className="w-10 h-14 rounded-md object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-14 rounded-md bg-white/5 flex-shrink-0 flex items-center justify-center">
                      <Video size={14} className="text-zinc-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{v.title || "Sem título"}</p>
                    <p className="text-zinc-500 text-xs truncate">{v.artist_name || "Artista"}</p>
                    <div className="mt-1 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#FF007F]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0 min-w-[80px]">
                    <span className="text-[#FF007F] font-bold text-sm tabular-nums flex items-center gap-1">
                      <Eye size={11} /> {(v.views || 0).toLocaleString("pt-BR")}
                    </span>
                    <span className="text-pink-300 text-[11px] tabular-nums flex items-center gap-1">
                      <Heart size={10} /> {(v.likes || 0).toLocaleString("pt-BR")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination total={filtered.length} page={page} onPage={setPage} idPrefix="videos" />
        </>
      )}
    </div>
  );
}

function DailyChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5">
        <h3 className="font-bold text-lg text-white mb-3">Plays por Dia</h3>
        <p className="text-zinc-500 text-sm">Sem dados ainda no período.</p>
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d.plays), 1);
  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5">
      <h3 className="font-bold text-lg text-white mb-4">Plays por Dia</h3>
      <div className="flex items-end gap-1 h-40" data-testid="stats-daily-chart">
        {data.map((d) => {
          const h = (d.plays / max) * 100;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.plays}`}>
              <div className="w-full rounded-t-sm bg-gradient-to-t from-[#FF007F] to-[#FF3399]" style={{ height: `${h}%`, minHeight: "2px" }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-[10px] text-zinc-600">
        <span>{data[0].date.slice(5)}</span>
        <span>{data[data.length - 1].date.slice(5)}</span>
      </div>
    </div>
  );
}

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const header = Object.keys(rows[0]);
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header.join(";"), ...rows.map((r) => header.map((h) => escape(r[h])).join(";"))].join("\n");
  // Add BOM for Excel pt-BR
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function AdminStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allVideos, setAllVideos] = useState([]);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/stats/dashboard?days=${days}&top_limit=500`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  // Carrega TODOS os vídeos uma vez para exportar planilha completa
  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/admin/video-submissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAllVideos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAllVideos([]));
  }, []);

  const videosCsvRows = useMemo(() => {
    return [...allVideos]
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .map((v, i) => ({
        posicao: i + 1,
        artista: v.artist_name || "",
        titulo: v.title || "",
        status: v.status || "",
        views: v.views || 0,
        curtidas: v.likes || 0,
        url_video: v.video_url || "",
        url_original: v.instagram_source_url || "",
        created_at: v.created_at || "",
      }));
  }, [allVideos]);

  const exportVideosCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`vitrine-videos-${stamp}.csv`, videosCsvRows);
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black flex items-center gap-2">
              <BarChart3 size={32} className="text-[#FF007F]" /> Estatísticas
            </h1>
            <p className="text-zinc-500 mt-1 text-sm">Plays, downloads e Vitrine de Vídeos na plataforma.</p>
          </div>
          <div className="flex gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-4 py-2 rounded-full text-sm font-bold transition ${
                  days === d ? "bg-[#FF007F] text-white" : "bg-white/5 text-zinc-400 hover:bg-white/10"
                }`}
                data-testid={`stats-period-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data ? (
          <p className="text-red-400">Falha ao carregar estatísticas.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card icon={PlayCircle} label="Plays totais" value={data.total_plays} accent="#FF007F" testId="stat-card-plays" />
              <Card icon={Download} label="Downloads totais" value={data.total_downloads} accent="#10B981" testId="stat-card-downloads" />
              <Card icon={TrendingUp} label="CDs no top" value={data.top_cds_by_plays?.length || 0} accent="#00E5FF" testId="stat-card-top-cds" />
              <Card icon={Music2} label="Período" value={`${data.period_days} dias`} accent="#F59E0B" testId="stat-card-period" />
            </div>

            {/* Cards Vitrine de Vídeos */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <Card icon={Video} label="Vídeos da Vitrine" value={data.total_videos || 0} accent="#8B5CF6" testId="stat-card-total-videos" />
              <Card icon={Eye} label="Views Vitrine" value={data.total_video_views || 0} accent="#00E5FF" testId="stat-card-video-views" />
              <Card icon={Heart} label="Curtidas Vitrine" value={data.total_video_likes || 0} accent="#FF007F" testId="stat-card-video-likes" />
            </div>

            <DailyChart data={data.daily_plays} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
              <TopList
                title="CDs mais tocados"
                items={data.top_cds_by_plays}
                countKey="plays"
                coverKey="cover_url"
                idPrefix="cds-plays"
              />
              <TopList
                title="CDs mais baixados"
                items={data.top_cds_by_downloads}
                countKey="downloads"
                coverKey="cover_url"
                idPrefix="cds-downloads"
              />
            </div>

            <div className="mt-6">
              <TopList title="Músicas mais tocadas" items={data.top_tracks_by_plays} countKey="plays" idPrefix="tracks-plays" />
            </div>

            {/* Top Vídeos + export CSV */}
            <div className="mt-6">
              <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-black text-white">Relatório — Vitrine do Arrocha</h2>
                  <p className="text-zinc-500 text-xs">Views e curtidas dos vídeos. Baixe a planilha CSV completa abaixo.</p>
                </div>
                <button
                  onClick={exportVideosCsv}
                  disabled={videosCsvRows.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black rounded-full text-sm font-bold transition"
                  data-testid="export-videos-csv-btn"
                  title="Exportar planilha CSV de todos os vídeos da Vitrine (views + curtidas)"
                >
                  <FileDown size={16} /> Baixar planilha CSV ({videosCsvRows.length})
                </button>
              </div>
              <TopVideosList items={data.top_videos_by_views} />
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
